{#each Object.entries($catMap) as [k, v] (k)}
    {#await readForm(parseCatMapKey(k)) then data}
        {#await isEventRestricted(parseCatMapKey(k)) then restricted}
            {#each range(v) as i (`${k}:${i}`)}
                {#if !!data || restricted}
                    <div class="card mt-4 bg-light" transition:fade|local>
                        <div class="py-2 px-4">
                            <div class="title-div bold">
                                { findNameForKey($_, $events, k) }
                            </div>

                            {#if restricted}
                                <UnseatedCouponForm ownKey={`${k}:${i}`} bind:this={coupons[`${k}:${i}`]} {readAllCoupons}>
                                    {#if !!data}
                                        <form bind:this={forms[`${k}:${i}`]} on:submit={(e) => e.preventDefault()} class="coupon-form">
                                            <DOMNode runScripts={true} node={parser.parseFromString(data, "text/html")}/> 
                                        </form>
                                    {/if}
                                </UnseatedCouponForm>
                            {:else}
                                <br>
                                <form bind:this={forms[`${k}:${i}`]} on:submit={(e) => e.preventDefault()}>
                                    <DOMNode runScripts={true} node={parser.parseFromString(data, "text/html")}/> 
                                </form>
                            {/if}
                        </div>
                    </div>
                {:else}
                    <div bind:this={forms[`${k}:${i}`]} style="display: none;" />
                {/if}
            {/each}
        {/await}
    {/await}
{/each}

<style lang="less">
    .bold {
        font-weight: 700;
    }

    .coupon-form {
        padding-top: 1.5rem;
    }
</style>

<script lang="ts">
    import GetFormURL from "tm:~endpoint:{controller: 'customized/api', action: 'read_form', event_id: ':event_id'}"
    import CheckRestrictionURL from "tm:~endpoint:{controller: 'customized/api', action: 'check_drafts', event_id: ':event_id'}"
    import client, { replaceURLParams } from "@/utils/http";
    import { catMap, setFormRetriever, setCouponRetriever } from "./unseated";
    import { onDestroy, onMount } from "svelte";
    import { EventPriceInformation, Shop } from "@ticketmachine/sdk";
    import { derived } from "svelte/store";
    import { depromise } from "@/utils/svelte/proxies";
    import DOMNode from "@/components/DOMNode.svelte";
    import { fade } from "svelte/transition";
    import { _, Translator } from "@frontend/translations";
    import UnseatedCouponForm from "./UnseatedCouponForm.svelte";

    const parser = new DOMParser();

    const formMap = new Map<number, Promise<string|null>>();
    async function readForm(event_id: number): Promise<string|null> {
        if (!formMap.has(event_id)) {
            formMap.set(event_id, (async () => {
                const { data } = await waitLoad(async () => await client.get<{data: string|null}>(replaceURLParams(GetFormURL, {event_id})));

                const form = data.data;
                if (form === null) return null;
                return form;
            })());
        }

        return await formMap.get(event_id)!;
    }

    const restrictionMap = new Map<number, Promise<boolean>>();

    let loadOngoing = 0;
    async function waitLoad<T>(p: () => Promise<T>): Promise<T> {
        try {
            loadOngoing += 1;
            return await p();
        } finally {
            loadOngoing -= 1;
        }
    }

    async function isEventRestricted(event_id: number): Promise<boolean> {
        if (!restrictionMap.has(event_id)) {
            restrictionMap.set(event_id, (async () => {
                return await waitLoad(async () => {
                    const { data } = await client.get<{ restricted: boolean}>(replaceURLParams(CheckRestrictionURL, {event_id}));
                    return data.restricted;
                });
            })());
        }
        return await restrictionMap.get(event_id)!;
    }

    function range(length: number): number[] {
        return Array.from(new Array(length)).map((_, i) => i);
    }

    function readAllCoupons() {
        const cforms: Record<string, UnseatedCouponForm> = {};
        for (let [k, count] of Object.entries($catMap)) {
            for (let i of range(count)) {
                const key = `${k}:${i}`
                if (!!coupons[key])
                    cforms[key] = coupons[key];
            }
        }
        return cforms;
    }

    function parseCatMapKey(key: string, idx=0): number {
        return Number(key.split("-")[idx]);
    }

    function toObject(data: FormData): Record<string, string> {
        const result: Record<string, string> = {}
        data.forEach((v, k) => result[k.substring(1, k.length-1)] = v.toString());
        return result;
    }

    function findNameForKey(tl: Translator, evs: EventPriceInformation[], key: string): string {
        const items = key.split("-").map(n => Number(n));
        const ev = evs.find((e) => e.id === items[0]);
        if (ev === null || ev === undefined) return "";
    
        const cat = ev.categories.find((c) => c.id === items[1]);
        if (cat === null || cat === undefined) return "Unknown. This shouldn't happen...";
        if (items.length == 2) {
            return tl("unseated.form._header.no-discount", [ev.name, cat.name], ["event", "category"]);
        }
    
        const disc = cat.discounts.find((d) => d.id === items[2]);
        if (disc === null || disc === undefined) return "Unknown. This shouldn't happen...";
    
        return tl("unseated.form._header.discount", [ev.name, cat.name, disc.name], ["event", "category", "discount"]);
    }

    $: expectedFormCount = Object.values($catMap).reduce((p, n) => n+p, 0);

    const event_ids = derived(catMap, (c) => Array.from(new Set(Object.keys(c).map(k => k.split("-")[0]).map((n) => Number(n)))));
    const events = depromise([], derived(event_ids, (eids) => Promise.all(eids.map(e => Shop.current.getEventPrices(e)))));
    
    const forms: Record<string, HTMLFormElement|HTMLDivElement> = {};
    const coupons: Record<string, UnseatedCouponForm> = {};

    function* getEntries(): Generator<string> {
        for (let [k, v] of Object.entries($catMap)) {
            for (let i = 0; i<v; i++) {
                yield `${k}:${i}`;
            }
        }
    }

    onMount(() => {
        setFormRetriever(() => {
            if (loadOngoing > 0) {
                alert($_("unseated.to_cart_disabled.page_is_loading"));
                return null;
            }

            const keys = [...getEntries()]
            const entries = keys.map(e => [e, forms[e]||{}]);
            if (entries.length != expectedFormCount) return null;
            const actualForms = Object.fromEntries(entries.filter(([_, f]) => f instanceof HTMLFormElement)) as Record<string, HTMLFormElement>;
            const invalidForms = Object.values(actualForms).filter((f) => !f.checkValidity());
            if (invalidForms.length > 0) {
                invalidForms.forEach(f => f.reportValidity());
                return null;
            };

            return Object.fromEntries(Object.entries(actualForms).map(([k, f]) => [k, toObject(new FormData(f))]));
        });

        setCouponRetriever(() => {
            if (loadOngoing > 0) {
                alert($_("unseated.to_cart_disabled.page_is_loading"));
                return null;
            }

            const keys = [...getEntries()]
            const couponCodes = keys.map(e => [e, coupons[e]||null]);
            const actualCoupons = couponCodes.filter(([_, f]) => !!f) as [string, UnseatedCouponForm][];
            const invalidCoupons = actualCoupons.filter(([_, f]) => !f.isValid());
            if (invalidCoupons.length > 0) {
                invalidCoupons.forEach(([_, f]) => f.reportValidity());
                return null;
            }
            return Object.fromEntries(actualCoupons.map(([k, f]) => [k, f.getCouponCode()]));
            
        });
    });

    onDestroy(() => {
        setFormRetriever(null);
        setCouponRetriever(null);
    });

</script>
